import { useMemo } from "react";

import { LOCALE_OPTIONS, OPTIONS_MULTIPLIER } from "~/v2/constants/optionOrder";
import type { MultiLegOrderSchema } from "../schema";
import type { CombinedLeg } from "../utils";
import { calculateMultiLegFeeAmounts } from "./useFees";

export interface MultiLegEstimatedCostResult {
  estimatedCost: string;
  isCredit: boolean;
}

export interface GetCostAndFeesArgs {
  legs: CombinedLeg[];
  quantity: number;
  price?: number;
}

export const getCostAndFees = ({
  legs,
  quantity,
  price = 0,
}: GetCostAndFeesArgs): MultiLegEstimatedCostResult => {
  const safePrice = Number.isNaN(price) ? 0 : price;
  const safeQuantity = Number.isNaN(quantity) ? 0 : quantity;

  const feeAmounts = calculateMultiLegFeeAmounts(legs, quantity);

  const estimatedCost =
    safePrice * OPTIONS_MULTIPLIER * safeQuantity + feeAmounts.totalFees;

  return {
    estimatedCost: Math.abs(estimatedCost).toLocaleString(
      "en-US",
      LOCALE_OPTIONS,
    ),
    isCredit: estimatedCost < 0,
  };
};

export const getMarketPrice = (legs: CombinedLeg[]) =>
  legs.reduce(
    (acc, { form: { positionIntent, ratioQty }, data: { contract } }) =>
      acc +
      (positionIntent?.includes("buy")
        ? (contract?.snapshot?.latestQuote?.ap ?? 0)
        : (contract?.snapshot?.latestQuote?.bp ?? 0) * -1) *
        (Number.isNaN(ratioQty) ? 0 : ratioQty),
    0,
  );

interface UseEstimatedCostParams {
  legs: CombinedLeg[];
  quantity: number;
  orderType: MultiLegOrderSchema["orderType"];
  limitPrice: number | undefined;
}

export const useEstimatedCost = ({
  legs,
  quantity,
  orderType,
  limitPrice,
}: UseEstimatedCostParams): MultiLegEstimatedCostResult => {
  return useMemo(
    () =>
      getCostAndFees({
        legs,
        quantity,
        price: orderType === "limit" ? limitPrice : getMarketPrice(legs),
      }),
    [legs, quantity, orderType, limitPrice],
  );
};
